/**
 * CardSight scan → card entry. Two modes:
 *
 * SINGLE (📷 Scan inside the Card form, unchanged behavior): capture/upload →
 * `identify-card` Edge Function (CardSight server-side) → hand name+number to
 * quickCardLookup() for pick-and-fill. CardSight is an identifier only.
 *
 * BULK (📷 Bulk Scan on the Cards toolbar, auto-accept flow): camera stays
 * open; each capture → CardSight → resolve against OUR catalog
 * (catalog_products). When exactly ONE catalog product matches
 * (name + number agree), the lot AUTO-SAVES with canonical product ID,
 * catalog image, and tonight's market price — zero taps. Ambiguous or
 * unidentified scans queue for review; each review row re-opens the classic
 * pick flow. Defaults for auto-added lots: Raw / Near Mint / Qty 1 /
 * Acq today / basis $0 (edit later).
 */
import { escapeHtml, toast } from "../lib/dom";
import { sb } from "../lib/supabase";
import { quickCardLookup } from "./quickCardLookup";
import { resolveCatalogProduct, getCatalogPrice } from "../lib/catalogResolve";
import { normalizeSetName } from "../lib/setNames";
import { queueImageCache } from "../lib/imageCache";
import { DB } from "../lib/db";
import { nextId } from "../utils/ids";
import { fmt$, todayISO } from "../utils/format";
import { render } from "../state";

const MAX_EDGE = 1568; // Anthropic/CardSight effective cap; keeps the number legible
let _stream: MediaStream | null = null;

function root(): HTMLElement {
  let r = document.getElementById("pickerRoot");
  if (!r) { r = document.createElement("div"); r.id = "pickerRoot"; document.body.appendChild(r); }
  return r;
}

function stopCam(): void {
  if (_stream) { _stream.getTracks().forEach((t) => t.stop()); _stream = null; }
}

export function closeCardSightScan(): void {
  stopCam();
  _bulk = null;
  const r = document.getElementById("pickerRoot");
  if (r) r.innerHTML = "";
}

/** Segment for CardSight, read from the Card form's Game field (single mode). */
function currentSegment(): string {
  const form = document.getElementById("cardForm") as HTMLFormElement | null;
  const game = ((form?.querySelector('[name="Game"]') as HTMLSelectElement | null)?.value || "Pokemon").toLowerCase();
  return game.includes("magic") ? "magic" : "pokemon";
}

function shell(bodyHtml: string, title = "📷 Scan Card", subtitle = "Fill the frame with one card in even light, then capture. We'll identify it and open the matching printings."): void {
  root().innerHTML = `
    <div class="fixed inset-0 modal-backdrop flex items-start justify-center p-3 sm:p-6 overflow-y-auto" style="z-index:55">
      <div class="panel w-full max-w-3xl my-6 shadow-2xl">
        <div class="flex items-center justify-between p-4 border-b border-[var(--border)]">
          <div>
            <h3 class="font-semibold">${escapeHtml(title)}</h3>
            <div class="text-xs text-[var(--muted)] mt-1">${escapeHtml(subtitle)}</div>
          </div>
          <button class="btn btn-ghost !py-1 !px-2" id="_csClose" title="Close">✕</button>
        </div>
        <div class="p-4">${bodyHtml}</div>
      </div>
    </div>`;
  document.getElementById("_csClose")?.addEventListener("click", () => {
    const hadAdds = (_bulk?.added || 0) > 0;
    closeCardSightScan();
    if (hadAdds) render();
  });
}

export function openCardSightScan(): void {
  shell(`
    <div class="flex gap-2 mb-4">
      <button class="btn btn-primary flex-1" id="_csCamBtn">📷 Use Camera</button>
      <label class="btn btn-ghost flex-1 text-center cursor-pointer">⤒ Upload Image
        <input type="file" accept="image/*" id="_csFile" class="hidden">
      </label>
    </div>
    <div id="_csStage" class="text-sm text-[var(--muted)]">Choose camera or upload to start.</div>
  `);
  document.getElementById("_csCamBtn")?.addEventListener("click", startCamera);
  document.getElementById("_csFile")?.addEventListener("change", (e) => {
    const f = (e.target as HTMLInputElement).files?.[0];
    if (f) handleFile(f);
  });
}

async function startCamera(): Promise<void> {
  const stage = document.getElementById("_csStage");
  if (!stage) return;
  stage.innerHTML = `
    <div class="rounded-lg overflow-hidden bg-black aspect-[4/3]"><video id="_csVideo" autoplay playsinline muted class="w-full h-full object-cover"></video></div>
    <button class="btn btn-primary w-full mt-3" id="_csSnap">Capture</button>`;
  try {
    _stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: "environment", width: { ideal: 1920 }, height: { ideal: 1440 } },
    });
    (document.getElementById("_csVideo") as HTMLVideoElement).srcObject = _stream;
  } catch (err) {
    stage.innerHTML = `<div class="text-[var(--bad,#ff5b6e)] text-sm">Could not open the camera: ${escapeHtml((err as Error).message)}. Try Upload Image instead.</div>`;
    return;
  }
  document.getElementById("_csSnap")?.addEventListener("click", () => {
    const v = document.getElementById("_csVideo") as HTMLVideoElement;
    if (!v?.videoWidth) { toast("Camera not ready yet", "err"); return; }
    const blob = drawResized(v, v.videoWidth, v.videoHeight);
    if (blob) blob.then((b) => b && identify(b));
  });
}

async function handleFile(file: File): Promise<void> {
  const stage = document.getElementById("_csStage");
  if (stage) stage.textContent = "Preparing image…";
  const img = new Image();
  img.onload = async () => {
    const blob = await drawResized(img, img.naturalWidth, img.naturalHeight);
    if (blob) identify(blob);
  };
  img.onerror = () => toast("Could not read that image", "err");
  img.src = URL.createObjectURL(file);
}

/** Draw a video/image onto a canvas resized to MAX_EDGE and export a JPEG blob. */
function drawResized(src: CanvasImageSource, w: number, h: number): Promise<Blob | null> {
  const scale = Math.min(1, MAX_EDGE / Math.max(w, h));
  const cw = Math.round(w * scale), ch = Math.round(h * scale);
  const c = document.createElement("canvas");
  c.width = cw; c.height = ch;
  c.getContext("2d")!.drawImage(src, 0, 0, cw, ch);
  return new Promise((res) => c.toBlob((b) => res(b), "image/jpeg", 0.9));
}

/** POST an image to the identify-card function. Returns {name, number, confidence} or null + error msg. */
async function callIdentify(blob: Blob, segment: string): Promise<{ match: { name: string; number?: string; confidence?: string } | null; errMsg: string }> {
  const fd = new FormData();
  fd.append("image", blob, "card.jpg");
  fd.append("segment", segment);
  let data: any = null, errMsg = "";
  try {
    const res = await sb.functions.invoke("identify-card", { body: fd });
    data = res.data;
    if (res.error) {
      errMsg = res.error.message || "Identification failed";
      try { const j = await res.error.context?.json?.(); if (j?.error) errMsg = j.error; } catch { /* */ }
    }
  } catch (e) {
    errMsg = (e as Error).message;
  }
  if (data?.ok && data.match?.name) return { match: data.match, errMsg: "" };
  return { match: null, errMsg };
}

async function identify(blob: Blob): Promise<void> {
  stopCam();
  const stage = document.getElementById("_csStage");
  if (stage) stage.innerHTML = `<div class="py-6 text-center text-[var(--muted)]">🔎 Identifying card…</div>`;

  const { match, errMsg } = await callIdentify(blob, currentSegment());

  if (match) {
    const { name, number, confidence } = match;
    closeCardSightScan();
    toast(`Identified: ${name}${number ? " #" + number : ""} (${confidence})`, "ok");
    // Hand off to the existing lookup: search by name, narrow by number so the
    // grid shows the right printing(s) for the user to confirm (handles reprints).
    quickCardLookup(1, name, number ? { number } : undefined);
    return;
  }

  // No match (or error) → let the user fall back to the manual lookup.
  if (stage) {
    const msg = errMsg
      ? `Couldn't identify the card: ${escapeHtml(errMsg)}.`
      : `Couldn't confidently identify the card.`;
    stage.innerHTML = `
      <div class="text-sm mb-3">${msg} You can search manually instead.</div>
      <div class="flex gap-2">
        <button class="btn btn-primary flex-1" id="_csManual">Search manually</button>
        <button class="btn btn-ghost flex-1" id="_csRetry">Try another photo</button>
      </div>`;
    document.getElementById("_csManual")?.addEventListener("click", () => { closeCardSightScan(); quickCardLookup(); });
    document.getElementById("_csRetry")?.addEventListener("click", openCardSightScan);
  }
}

/* ============================================================
   BULK SCAN — auto-accept flow
   ============================================================ */

interface BulkReviewItem { seed: string; number?: string; label: string; }
interface BulkState {
  game: "Pokemon" | "Magic: The Gathering";
  added: number;
  reviews: BulkReviewItem[];
  log: string[];          // newest-first display lines
  busy: boolean;
}
let _bulk: BulkState | null = null;

export function openBulkScan(): void {
  _bulk = { game: "Pokemon", added: 0, reviews: [], log: [], busy: false };
  shell(`
    <div class="flex gap-2 items-center mb-3">
      <label class="label !mb-0">Game</label>
      <select class="select !w-56" id="_csbGame">
        <option>Pokemon</option>
        <option>Magic: The Gathering</option>
      </select>
      <span class="text-xs text-[var(--muted)] ml-auto" id="_csbCount"></span>
    </div>
    <div class="rounded-lg overflow-hidden bg-black aspect-[4/3]"><video id="_csbVideo" autoplay playsinline muted class="w-full h-full object-cover"></video></div>
    <button class="btn btn-primary w-full mt-3" id="_csbSnap">Capture card</button>
    <div class="text-xs text-[var(--muted)] mt-1 text-center">Exact matches save themselves — just keep capturing. Unsure ones queue below.</div>
    <div id="_csbList" class="mt-3 space-y-1 text-sm max-h-56 overflow-y-auto"></div>
  `, "📷 Bulk Scan", "Scan a stack: capture, next card, capture. High-confidence matches auto-save with price + catalog link.");

  document.getElementById("_csbGame")?.addEventListener("change", (e) => {
    if (_bulk) _bulk.game = (e.target as HTMLSelectElement).value as BulkState["game"];
  });
  startBulkCamera();
}

async function startBulkCamera(): Promise<void> {
  try {
    _stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: "environment", width: { ideal: 1920 }, height: { ideal: 1440 } },
    });
    const v = document.getElementById("_csbVideo") as HTMLVideoElement | null;
    if (v) v.srcObject = _stream;
  } catch (err) {
    toast(`Camera unavailable: ${(err as Error).message}`, "err");
    return;
  }
  document.getElementById("_csbSnap")?.addEventListener("click", async () => {
    if (!_bulk || _bulk.busy) return;
    const v = document.getElementById("_csbVideo") as HTMLVideoElement;
    if (!v?.videoWidth) { toast("Camera not ready yet", "err"); return; }
    const blob = await drawResized(v, v.videoWidth, v.videoHeight);
    if (!blob) { toast("Capture failed - couldn't read a frame from the camera. Try again.", "err"); return; }
    void bulkIdentify(blob);
  });
}

function bulkRefreshUI(): void {
  if (!_bulk) return;
  const count = document.getElementById("_csbCount");
  if (count) count.textContent = `${_bulk.added} added · ${_bulk.reviews.length} to review`;
  const list = document.getElementById("_csbList");
  if (list) {
    const reviewRows = _bulk.reviews.map((r, i) => `
      <div class="panel-2 px-2 py-1 flex items-center gap-2">
        <span class="text-amber-300 shrink-0">?</span>
        <span class="truncate">${escapeHtml(r.label)}</span>
        <button class="btn btn-ghost !py-0.5 !px-2 text-xs ml-auto shrink-0" data-csb-review="${i}">Resolve</button>
      </div>`).join("");
    const logRows = _bulk.log.slice(0, 12).map(l => `<div class="px-2 py-0.5 text-[var(--muted)]">${l}</div>`).join("");
    list.innerHTML = reviewRows + logRows;
    list.querySelectorAll<HTMLElement>("[data-csb-review]").forEach(btn => {
      btn.addEventListener("click", () => {
        const item = _bulk?.reviews[Number(btn.dataset.csbReview)];
        if (!item) return;
        closeCardSightScan();
        render();
        // Open a fresh Add Card form, then run the classic pick flow with the seed.
        (window as any).editCard?.();
        if (item.seed) quickCardLookup(1, item.seed, item.number ? { number: item.number } : undefined);
      });
    });
  }
}

const BULK_DEFAULTS = {
  "Inventory Class": "Raw",
  "Status": "Available",
  "Condition/Grade": "Near Mint",
  "Acq Type": "Purchase",
  "Source ID": "",
  "Notes": "Added via bulk scan",
};

async function bulkIdentify(blob: Blob): Promise<void> {
  if (!_bulk) return;
  _bulk.busy = true;
  const snap = document.getElementById("_csbSnap") as HTMLButtonElement | null;
  if (snap) { snap.disabled = true; snap.textContent = "🔎 Identifying…"; }

  try {
    const segment = _bulk.game.includes("Magic") ? "magic" : "pokemon";
    const { match, errMsg } = await callIdentify(blob, segment);

    if (!match) {
      _bulk.log.unshift(`✗ Not identified${errMsg ? ` (${escapeHtml(errMsg)})` : ""} — try again with better light`);
      toast("Couldn't identify — try again", "err");
      return;
    }

    const name = match.name || "";
    const number = match.number || "";
    const cat = await resolveCatalogProduct({ game: _bulk.game, name, number });

    if (!cat) {
      // Identified but ambiguous/unmatched in catalog → review queue.
      _bulk.reviews.push({ seed: name, number: number || undefined, label: `${name}${number ? " #" + number : ""} — needs a pick` });
      toast(`Queued for review: ${name}`, "ok");
      return;
    }

    // AUTO-ACCEPT: exactly one catalog product.
    const price = await getCatalogPrice(cat.productId);
    const unit = price?.unit ?? 0;
    const already = DB.table("cards").find(r =>
      Number(r["TCG Product ID"]) === cat.productId && (Number(r["Qty"]) || 0) > 0);

    const row: Record<string, any> = {
      "Card Lot ID": nextId("C", "cards", "Card Lot ID"),
      "Game": _bulk.game,
      "Card Name": name,
      "Card #": cat.cardNumber || number,
      "Set": normalizeSetName(cat.groupName),
      "Acq Date": todayISO(),
      "Qty": 1,
      "Unit Basis": 0,
      "Total Basis": 0,
      "Current FMV": unit,
      "Unrealized G/L": unit,
      "Valuation Source": price ? "TCGplayer Market" : "",
      "TCG Product ID": cat.productId,
      "Image URL": cat.imageUrl || "",
      ...BULK_DEFAULTS,
    };
    void DB.add("cards", row);
    queueImageCache("cards", row);
    _bulk.added++;
    const dupNote = already ? ` · you already hold ${already["Card Lot ID"]}` : "";
    _bulk.log.unshift(`✓ ${escapeHtml(name)} #${escapeHtml(String(cat.cardNumber || number))} · ${fmt$(unit)}${dupNote}`);
    toast(`Added ${name} · ${fmt$(unit)}`, "ok");
  } finally {
    _bulk.busy = false;
    if (snap) { snap.disabled = false; snap.textContent = "Capture card"; }
    bulkRefreshUI();
  }
}

(window as any).openCardSightScan = openCardSightScan;
(window as any).openBulkScan = openBulkScan;
